import React, { Component } from 'react';
import { uniq } from 'lodash';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import Badge from 'components/v2/Badge';
import { titleize, humanize, unescapeHTML } from 'underscore.string';
import { massUpdateActions } from 'constants/MassUpdates';
import { getUpdateFieldForAction } from 'helpers/MassUpdates';
import CargomaticInput from 'components/v2/input';
import { withTranslation } from 'react-i18next';
import { t } from 'i18next';
import { sluggify } from 'utils/localization/utils';
import ErrorAlert from '../../ui/ErrorAlert';
import TagEditor from 'components/ui/TagEditor';
import AdminConstants from 'constants/AdminConstants';
import { TERMINAL_HOLD_STATUS_VALUES } from 'constants/terminalHoldStatus';
import TextField from 'components/v2/forms/TextField';
import CargomaticSelect from 'components/v2/select';
import ReferenceNumberTypes from 'constants/ReferenceNumberTypes';
import { isIntermodalShipmentType } from 'helpers/shipment-types';
import { getClassName } from 'utils/ui/classes';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
  DropdownMenuContent,
} from '@cargomatic/ui';
import { PlusCircleIcon } from 'lucide-react';

const SHORTHAUL_REF_NUMBERS = ReferenceNumberTypes.filter((r) => r.editable);
const EXCLUDED_REF_FIELDS = ['isa', 'truck_order', 'VRID', 'delivery_order'];
const defaultColumnHeaders = [
  { name: 'Shipment', localizationSlug: 'SHIPMENT', count: 1 },
  { name: 'Shipper', localizationSlug: 'SHIPPER' },
  { name: 'Equipment', localizationSlug: 'EQUIPMENT' },
];

const bulkAddReferenceHeaders = [
  { name: 'Shipment', localizationSlug: 'SHIPMENT' },
  { name: 'Shipper', localizationSlug: 'SHIPPER' },
  { name: 'Bulk Add Tags', localizationSlug: 'BULK_ADD_REFERENCE_VALUES' },
  { name: 'Apply To All', localizationSlug: 'APPLY_TO_ALL' },
  { name: 'Remove', localizationSlug: 'REMOVE' },
];

const bulkClearTagsHeaders = [
  { name: 'Shipment', localizationSlug: 'SHIPMENT' },
  { name: 'Shipper', localizationSlug: 'SHIPPER' },
  { name: 'Tags', localizationSlug: 'TAGS' },
  { name: 'Remove', localizationSlug: 'REMOVE' },
];
const bulkAddTagsHeaders = [
  { name: 'Shipment', localizationSlug: 'SHIPMENT' },
  { name: 'Shipper', localizationSlug: 'SHIPPER' },
  { name: 'Tags', localizationSlug: 'TAGS' },
  { name: 'Bulk Add Tags', localizationSlug: 'BULK_ADD_TAGS' },
  { name: 'Apply To All', localizationSlug: 'APPLY_TO_ALL' },
  { name: 'Remove', localizationSlug: 'REMOVE' },
];
const bulkAddTerminalHoldsHeaders = [
  { name: 'Shipment', localizationSlug: 'SHIPMENT' },
  { name: 'Shipper', localizationSlug: 'SHIPPER' },
  { name: 'Terminal Holds', localizationSlug: 'TERMINAL_HOLDS' },
  {
    name: 'Add Terminal Holds',
    localizationSlug: 'ADD_TERMINAL_HOLDS',
  },
  { name: 'Apply To All', localizationSlug: 'APPLY_TO_ALL' },
  { name: 'Remove', localizationSlug: 'REMOVE' },
];
const disableApplyToAllForLast = [
  massUpdateActions.BULK_ADD_TAGS,
  massUpdateActions.BULK_ADD_TERMINAL_HOLDS,
  massUpdateActions.BULK_ADD_REFERENCE_NUMBERS,
];

const disableApplyToAllIfEmpty = [massUpdateActions.BULK_ADD_REFERENCE_NUMBERS];

const hideClearAllActions = [massUpdateActions.BULK_CLEAR_TAGS];

class ShipmentsGroup extends Component {
  static propTypes = {
    updateAction: PropTypes.string.isRequired,
    shipmentUpdates: PropTypes.object,
    shipments: PropTypes.array.isRequired,
    shipmentUpdateResults: PropTypes.array,
    title: PropTypes.string,
    saving: PropTypes.bool,
    disableSave: PropTypes.bool,
    onClearAll: PropTypes.func.isRequired,
    onSaveAll: PropTypes.func.isRequired,
    onApplyToAll: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
    shipperReferenceNumbers: PropTypes.array,
    handleReferenceNumberTypeChange: PropTypes.func.isRequired,
    referenceNumberType: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      notes: {},
    };
  }

  getActionName() {
    const { updateAction, t } = this.props;

    switch (updateAction) {
      case massUpdateActions.SHIPMENT_ARRIVED:
        return t('ARRIVE_ALL');
      case massUpdateActions.BULK_CLEAR_TAGS:
        return t('CLEAR_TAGS');
      default:
        return t('SAVE_ALL');
    }
  }

  getLocalisedHeaders(headers = []) {
    return headers?.map((header) =>
      header.count
        ? t(header.localizationSlug, { count: header.count })
        : t(header.localizationSlug),
    );
  }

  getHeaders() {
    const { updateAction, t } = this.props;
    const localizationSlug =
      Object.keys(massUpdateActions).find(
        (key) => massUpdateActions[key] === updateAction,
      ) || '';
    const defaultColumnHeadersLocalization = defaultColumnHeaders.map(
      (header) =>
        header.count
          ? t(header.localizationSlug, { count: header.count })
          : t(header.localizationSlug),
    );
    const bulkClearTagsHeadersLocalization =
      this.getLocalisedHeaders(bulkClearTagsHeaders);
    const bulkAddTagsHeadersLocalization =
      this.getLocalisedHeaders(bulkAddTagsHeaders);
    const bulkAddTerminalHoldsHeadersLocalization = this.getLocalisedHeaders(
      bulkAddTerminalHoldsHeaders,
    );
    const bulkAddReferenceLocalization = this.getLocalisedHeaders(
      bulkAddReferenceHeaders,
    );
    switch (updateAction) {
      case massUpdateActions.SHIPMENT_ARRIVED:
      case massUpdateActions.EDIT_ESTIMATED_ARRIVAL:
      case massUpdateActions.EDIT_ARRIVAL:
      case massUpdateActions.EDIT_DISCHARGE:
      case massUpdateActions.EDIT_LAST_FREE_DAY:
      case massUpdateActions.EDIT_LFD_AT_RAIL_FACILITY:
      case massUpdateActions.EDIT_ETA_TO_FINAL_RAIL_FACILITY:
      case massUpdateActions.BULK_APPLY_ADMIN_NOTES:
      case massUpdateActions.BULK_APPLY_SPECIAL_INSTRUCTIONS:
      case massUpdateActions.UPDATE_LAST_CUT:
      case massUpdateActions.UPDATE_EARLIEST_RETURN:
      case massUpdateActions.UPDATE_EARLIEST_GET_EMPTY:
      case massUpdateActions.UPDATE_EMPTY_READY_FOR_PICKUP:
      case massUpdateActions.UPDATE_EMPTY_RETURN:
      case massUpdateActions.UPDATE_REVIEW_DATE:
      case massUpdateActions.UPDATE_ACCOUNTING_REVIEW_DATE:
        return [
          ...defaultColumnHeadersLocalization,
          titleize(t(localizationSlug)),
          t('APPLY_TO_ALL'),
          titleize(t('REMOVE')),
        ];
      case massUpdateActions.BULK_CLEAR_TAGS:
        return bulkClearTagsHeadersLocalization;
      case massUpdateActions.BULK_ADD_TAGS:
        return bulkAddTagsHeadersLocalization;
      case massUpdateActions.BULK_ADD_TERMINAL_HOLDS:
        return bulkAddTerminalHoldsHeadersLocalization;
      case massUpdateActions.BULK_ADD_REFERENCE_NUMBERS:
        return bulkAddReferenceLocalization;
      default:
        return [...defaultColumnHeadersLocalization];
    }
  }

  getValidationClass(shipment, field) {
    const { status } = shipment;

    if (status[field]) {
      return {
        error: true,
      };
    }
    return {
      error: false,
    };
  }

  handleRemoveShipment = (shipment) => () =>
    this.props.onRemoveShipment(shipment);

  handleClearAll = () => this.props.onClearAll();

  handleApplyToAll = (shipmentId) => () => this.props.onApplyToAll(shipmentId);

  handleSaveAll = () => this.props.onSaveAll();

  handleDateFieldChange = (jobId, field) => (momentDateObj) => {
    const dateString = momentDateObj ? momentDateObj.toISOString() : null;
    this.props.onFieldChange(jobId, field, dateString);
  };

  handleTextChange = (jobId) => (inputString) => {
    this.setState({ notes: { [jobId]: inputString } });
  };

  handleCustomReferenceNumberChange = (value) => {
    this.props.handleReferenceNumberTypeChange('custom', value);
  };

  showClearAll() {
    const { updateAction } = this.props;
    return !hideClearAllActions.includes(updateAction);
  }

  renderColumns(shipment) {
    const { updateAction } = this.props;

    switch (updateAction) {
      case massUpdateActions.SHIPMENT_ARRIVED:
      case massUpdateActions.EDIT_ESTIMATED_ARRIVAL:
      case massUpdateActions.EDIT_ARRIVAL:
      case massUpdateActions.EDIT_DISCHARGE:
      case massUpdateActions.EDIT_LAST_FREE_DAY:
      case massUpdateActions.EDIT_ETA_TO_FINAL_RAIL_FACILITY:
      case massUpdateActions.EDIT_LFD_AT_RAIL_FACILITY:
      case massUpdateActions.UPDATE_LAST_CUT:
      case massUpdateActions.UPDATE_EARLIEST_RETURN:
      case massUpdateActions.UPDATE_REVIEW_DATE:
      case massUpdateActions.UPDATE_EMPTY_RETURN:
      case massUpdateActions.UPDATE_ACCOUNTING_REVIEW_DATE:
      case massUpdateActions.UPDATE_EARLIEST_GET_EMPTY: {
        const field = getUpdateFieldForAction(updateAction);
        return this.renderDateShipmentColumns(shipment, field);
      }
      case massUpdateActions.UPDATE_EMPTY_READY_FOR_PICKUP: {
        const field = getUpdateFieldForAction(updateAction);
        return this.renderDateShipmentColumns(shipment, field);
      }
      case massUpdateActions.BULK_APPLY_ADMIN_NOTES:
      case massUpdateActions.BULK_APPLY_SPECIAL_INSTRUCTIONS: {
        const field = getUpdateFieldForAction(updateAction);
        return this.renderTextInputColumns(shipment, field);
      }
      case massUpdateActions.BULK_CLEAR_TAGS: {
        return this.renderClearTagsColumns(shipment);
      }
      case massUpdateActions.BULK_ADD_TAGS: {
        return this.renderAddTagsColumns(shipment);
      }
      case massUpdateActions.BULK_ADD_TERMINAL_HOLDS: {
        return this.renderBulkAddTerminalHoldsColumns(shipment);
      }
      case massUpdateActions.BULK_ADD_REFERENCE_NUMBERS: {
        return this.renderAddReferenceColumns(shipment);
      }

      default:
        return renderDefaultColumns(shipment);
    }
  }

  renderDateShipmentColumns(shipment, field) {
    return [
      ...renderDefaultColumns(shipment),
      this.renderDateInput(shipment, field),
      this.renderApplyToAllButton(shipment),
      this.renderRemoveShipmentButton(shipment),
    ];
  }

  renderTextInputColumns(shipment, field) {
    return [
      ...renderDefaultColumns(shipment),
      this.renderTextInput(shipment, field),
      this.renderApplyToAllButton(shipment),
      this.renderRemoveShipmentButton(shipment),
    ];
  }

  renderAddBulkTags(shipment, field) {
    const { _id: jobId } = shipment;
    const { shipmentUpdates, shipmentUpdateResults } = this.props;
    const newTagsValue = shipmentUpdates?.[jobId]?.[field] || [];
    const emptyFields =
      shipmentUpdateResults?.flatMap((result) => result.job_ids) || [];
    const isEmpty = emptyFields.includes(jobId);
    return (
      <td key={`td-tag-editor-${jobId}`}>
        <TagEditor
          key={`Tag-Editor-${jobId}`}
          tags={newTagsValue}
          handleChange={(shipment, tags) => {
            const { _id: jobId } = shipment;
            this.props.onFieldChange(jobId, field, tags);
          }}
          object={shipment}
          suggestions={AdminConstants.shipmentTagSuggestions}
          wrapperClasses={isEmpty ? 'border-2 border-red-500 rounded-md' : ''}
        />
      </td>
    );
  }

  renderAddTerminalHoldsStatus(shipment, field) {
    const { _id: jobId } = shipment;
    const { t, shipmentUpdates, onFieldChange } = this.props;
    const terminalHoldsValue = shipmentUpdates?.[jobId]?.[field] || [];

    const handleSelectTerminalHoldStatus = (terminalHold) => {
      return (checked) => {
        if (checked) {
          const newValues = uniq(terminalHoldsValue.concat(terminalHold));
          onFieldChange(jobId, field, newValues);
        } else {
          const newValues = terminalHoldsValue.filter(
            (current) => current !== terminalHold,
          );
          onFieldChange(jobId, field, newValues);
        }
      };
    };

    return (
      <td key={`td-bulk-terminal-hold-editor-${jobId}`}>
        <DropdownMenu className="mb-4">
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-10 gap-1">
              <span className="sr-only sm:not-sr-only sm:whitespace-nowrap">
                {t('TERMINAL_HOLDS_STATUS')}
              </span>
              <PlusCircleIcon className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="z-[9999] max-h-72 overflow-y-auto"
          >
            <DropdownMenuLabel>{t('TERMINAL_HOLDS_STATUS')}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {TERMINAL_HOLD_STATUS_VALUES.map((terminalHold) => (
              <DropdownMenuCheckboxItem
                key={terminalHold}
                onCheckedChange={handleSelectTerminalHoldStatus(terminalHold)}
                checked={terminalHoldsValue.includes(terminalHold)}
              >
                <span data-testid={`dropdown-option-${terminalHold}`}>
                  {t(sluggify(terminalHold))}
                </span>
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    );
  }

  renderReferenceNumberDropdown(shipment) {
    const { t, shipperReferenceNumbers, referenceNumberType } = this.props;
    const isIntermodalShipment = isIntermodalShipmentType(
      shipment?.shipment_type,
    );

    let displayedFields = SHORTHAUL_REF_NUMBERS.filter(
      (field) => !EXCLUDED_REF_FIELDS.includes(field.name),
    );
    if (isIntermodalShipment) {
      displayedFields = shipperReferenceNumbers;
    }
    displayedFields = displayedFields
      .sort((a, b) => a.name.localeCompare(b.name))
      .concat([
        {
          name: 'custom',
          localizationSlug: titleize(t('CUSTOM')),
        },
      ]);
    const referenceNumberRequired =
      !referenceNumberType.custom && !referenceNumberType.name
        ? t('REFERENCE_NUMBER_COMPULSORY')
        : '';
    const customReferenceRequired = !referenceNumberType.customReferenceNumber
      ? t('CUSTOM_REFERENCE_NUMBER_REQUIRED')
      : '';
    const selectClass = getClassName('w-full mt-1', {
      'border-red-500': referenceNumberRequired,
      'text-red-500': referenceNumberRequired,
    });

    return (
      <div className="flex gap-2">
        <div className="w-1/2">
          <CargomaticSelect
            showCaption
            label={t('REFERENCE_NUMBER_TYPE')}
            selected={referenceNumberType.name}
            className={'w-full'}
            inputClassNames={selectClass}
            items={displayedFields}
            onSelect={(item) => {
              this.props.handleReferenceNumberTypeChange(item.name, '');
            }}
            applyDefaultMargins={false}
            valueKey="name"
            textKey="localizationSlug"
            error={{ message: referenceNumberRequired }}
            dataTestId={'select-reference-number-uv'}
            caption={t('SELECT_A_TYPE')}
          />
        </div>

        {referenceNumberType.custom && (
          <div className="w-1/2">
            <CargomaticInput
              dataTestId="CargomaticInput-0406967e"
              error={{ message: customReferenceRequired }}
              className="mb-2"
              label={t('CUSTOM_REFERENCE_NUMBER_TYPE')}
              type="text"
              placeholder={t('REF_#_NAME')}
              onChange={this.handleCustomReferenceNumberChange}
              value={unescapeHTML(
                referenceNumberType?.customReferenceNumber || '',
              )}
            />
          </div>
        )}
      </div>
    );
  }

  renderEditReferenceNumber(shipment, field) {
    const { _id: jobId } = shipment;
    const { shipmentUpdates, referenceNumberType } = this.props;
    const refNumber = shipmentUpdates?.[jobId]?.[field] || [];
    const referenceNumberExists = referenceNumberType.custom
      ? referenceNumberType.customReferenceNumber
      : referenceNumberType.name;
    const disabled = !referenceNumberExists;
    return (
      <td data-testid={`td-add-ref-numbers-${jobId}`}>
        <TextField
          disabled={disabled}
          dataTestId="TextField-31cf1f5f"
          type="text"
          value={unescapeHTML(refNumber)}
          placeholder={t('REF_#_VALUE')}
          onChange={(value) => {
            this.props.onFieldChange(jobId, field, value);
          }}
        />
      </td>
    );
  }

  renderShipmentNumber(shipment) {
    const { _id: id } = shipment;
    const idKey = `id_${id}`;
    const shipmentLink = `/shipments/${id}`;
    return [
      <td key={idKey}>
        <a href={shipmentLink} target="_blank" rel="noreferrer">
          {shipment.shipment_id}
        </a>
      </td>,
    ];
  }

  renderShipperName(shipment) {
    const { _id: id } = shipment;
    const shipperNameKey = `shipper_${id}`;
    return [<td key={shipperNameKey}>{shipment.shipper_name}</td>];
  }

  renderShipmentTags(shipment, field) {
    const { _id: id } = shipment;
    const tags = shipment?.[field]?.filter((tag) => tag) || [];
    return (
      <td>
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, tag_id) => (
            <div
              key={`${id}_${tag_id}`}
              className="px-2 py-1 bg-gray-200 rounded text-sm whitespace-nowrap"
            >
              {tag}
            </div>
          ))}
        </div>
      </td>
    );
  }

  renderShipmentTerminalHoldsStatus(shipment, field) {
    const { _id: jobId } = shipment;
    const { t, shipmentUpdates, onFieldChange } = this.props;
    const terminalHolds = shipmentUpdates?.[jobId]?.[field] || [];
    const handleRemoveTerminalHoldStatus = (terminalHold) => {
      const newValues = terminalHolds.filter(
        (current) => current !== terminalHold,
      );
      onFieldChange(jobId, field, newValues);
    };
    return (
      <td>
        <div className="flex flex-wrap gap-2">
          {terminalHolds.map((terminalHold) => (
            <Badge
              key={terminalHold}
              size="large"
              rounded
              withRemove
              onRemove={() => handleRemoveTerminalHoldStatus(terminalHold)}
            >
              <span data-testid={`badge-span-${terminalHold}`}>
                {titleize(t(sluggify(terminalHold)))}
              </span>
            </Badge>
          ))}
        </div>
      </td>
    );
  }

  renderClearTagsColumns(shipment) {
    return [
      this.renderShipmentNumber(shipment),
      this.renderShipperName(shipment),
      this.renderShipmentTags(shipment, 'tags'),
      this.renderRemoveShipmentButton(shipment),
    ];
  }

  renderAddTagsColumns(shipment) {
    return [
      this.renderShipmentNumber(shipment),
      this.renderShipperName(shipment),
      this.renderShipmentTags(shipment, 'tags'),
      this.renderAddBulkTags(shipment, 'newTags'),
      this.renderApplyToAllButton(shipment, 'newTags'),
      this.renderRemoveShipmentButton(shipment),
    ];
  }

  renderBulkAddTerminalHoldsColumns(shipment) {
    return [
      this.renderShipmentNumber(shipment),
      this.renderShipperName(shipment),
      this.renderShipmentTerminalHoldsStatus(shipment, 'terminal_hold_status'),
      this.renderAddTerminalHoldsStatus(shipment, 'terminal_hold_status'),
      this.renderApplyToAllButton(shipment, 'terminal_hold_status'),
      this.renderRemoveShipmentButton(shipment),
    ];
  }

  renderAddReferenceColumns(shipment) {
    return [
      this.renderShipmentNumber(shipment),
      this.renderShipperName(shipment),
      this.renderEditReferenceNumber(shipment, 'new_ref_val'),
      this.renderApplyToAllButton(shipment, 'new_ref_val'),
      this.renderRemoveShipmentButton(shipment),
    ];
  }

  renderTextInput(shipment, field) {
    const { shipmentUpdates } = this.props;
    const selectedNote =
      shipmentUpdates[shipment._id] && shipmentUpdates[shipment._id][field];

    const noteKey = `note_${shipment._id}_${selectedNote}`;
    const className = {
      'md:w-1/6': true,
      ...this.getValidationClass(shipment, 'text'),
    };

    return (
      <td key={noteKey} className={classnames(className)}>
        <div>
          <CargomaticInput
            type="textarea"
            key={noteKey}
            onBlur={() =>
              this.props.onFieldChange(
                shipment._id,
                field,
                this.state.notes[shipment._id],
              )
            }
            defaultValue={selectedNote}
            value={
              typeof this.state.notes[shipment._id] === 'string'
                ? this.state.notes[shipment._id]
                : selectedNote
            }
            placeholderText={field}
            onChange={this.handleTextChange(shipment._id)}
          />
        </div>
      </td>
    );
  }

  renderDateInput(shipment, field) {
    const { shipmentUpdates, t } = this.props;
    const selectedDate =
      shipmentUpdates[shipment._id] && shipmentUpdates[shipment._id][field];
    const datePickerKey = `datePicker_${shipment._id}_${selectedDate}`;
    const className = {
      ...this.getValidationClass(shipment, 'date'),
    };

    return (
      <td key={datePickerKey} className={classnames(className)}>
        <div>
          <DatePicker
            key={datePickerKey}
            selected={mapDateValue(selectedDate)}
            placeholderText={t(sluggify(humanize(field)))}
            className="w-full shadow border rounded px-3 -ml-3"
            showTimeSelect
            dateFormat="MM/DD/YYYY hh:mm a"
            timeIntervals={30}
            onChange={this.handleDateFieldChange(shipment._id, field)}
          />
        </div>
      </td>
    );
  }

  getDisableApplyToAll(shipment, field) {
    let disableApplyToAll = false;
    const { _id: jobId } = shipment;
    const { shipmentUpdates, action, shipments } = this.props;
    const lastShipment = shipments[shipments.length - 1];
    const shipmentUpdate = shipmentUpdates?.[jobId];
    if (action === massUpdateActions.BULK_ADD_TAGS) {
      disableApplyToAll = !shipmentUpdate?.[field]?.length;
    }
    if (action === massUpdateActions.BULK_ADD_TERMINAL_HOLDS) {
      disableApplyToAll = !shipmentUpdate?.[field]?.length;
    }
    if (disableApplyToAllIfEmpty.includes(action)) {
      disableApplyToAll = !shipmentUpdate?.[field];
    }
    if (
      disableApplyToAllForLast.includes(action) &&
      lastShipment._id === jobId
    ) {
      disableApplyToAll = true;
    }
    return disableApplyToAll;
  }

  renderApplyToAllButton(shipment, field) {
    const applyAllKey = `apply_${shipment._id}`;
    const { t } = this.props;
    const buttonClassName = {
      button: true,
      'button-hollow-green': true,
    };
    const disableApplyToAll = this.getDisableApplyToAll(shipment, field);
    return (
      <td key={applyAllKey}>
        <button
          type="button"
          data-testid={`${applyAllKey}-button`}
          className={classnames(buttonClassName)}
          onClick={this.handleApplyToAll(shipment._id, field)}
          disabled={disableApplyToAll}
        >
          {titleize(t('APPLY'))}
        </button>
      </td>
    );
  }

  renderRemoveShipmentButton(shipment) {
    const removeShipmentKey = `remove_${shipment._id}`;
    return (
      <td key={removeShipmentKey}>
        <button
          className="button button-secondary"
          onClick={this.handleRemoveShipment(shipment)}
        >
          <i className="fa fa-remove" />
        </button>
      </td>
    );
  }

  renderShipment(shipment) {
    const columns = this.renderColumns(shipment);
    return (
      <tr key={shipment._id} className="p-3">
        {columns}
      </tr>
    );
  }

  renderHeader() {
    const columnHeaders = this.getHeaders();
    return (
      <thead>
        <tr>
          {columnHeaders.map((col) => (
            <th
              key={col}
              data-testid={`header-${col}`}
              className="font-bold text-grey-darker"
            >
              {col}
            </th>
          ))}
        </tr>
      </thead>
    );
  }

  renderBody() {
    const { shipments } = this.props;

    return (
      <tbody>
        {shipments.map((shipment) => this.renderShipment(shipment))}
      </tbody>
    );
  }

  renderStatuses() {
    const { shipmentUpdateResults } = this.props;

    if (!shipmentUpdateResults || shipmentUpdateResults.length === 0) {
      return null;
    }

    return (
      <div className="shipment-status-wrapper flex flex-col mr-4">
        {shipmentUpdateResults.map((s) => renderStatus(s))}
      </div>
    );
  }

  render() {
    const {
      title,
      saving,
      disableSave,
      t,
      disableClearAll,
      shipments,
      action,
    } = this.props;

    const buttonDisabled = saving || disableSave;
    return (
      <div className="shadow bg-white p-4 mb-6">
        <div
          className="flex justify-between"
          data-testid="mass-update-shipment-modal-title"
        >
          <h4 className="mb-2 text-grey-darker">{title}</h4>
        </div>
        <hr />
        <div>
          {action === massUpdateActions.BULK_ADD_REFERENCE_NUMBERS &&
            this.renderReferenceNumberDropdown(shipments?.[0] || {})}
        </div>
        <hr />
        <div className="shipment-rows">
          <table className="table">
            {this.renderHeader()}
            {this.renderBody()}
          </table>
          <div className="flex justify-end items-baseline">
            {this.renderStatuses()}
            {this.showClearAll() && (
              <button
                className="button button-hollow-green mr-2"
                onClick={this.handleClearAll}
                disabled={disableClearAll}
              >
                {t('CLEAR_ALL')}
              </button>
            )}
            <button
              className={`button button-green flex items-center unifiedView-masterList-${this.props.updateAction}`}
              disabled={buttonDisabled}
              onClick={this.handleSaveAll}
              data-testid="mass-update-modal-action-button"
            >
              {saving && (
                <div className="flex items-center mr-2">
                  <i className="fa fa-spinner fa-spin" />
                </div>
              )}
              <span>{this.getActionName()}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function renderStatus(status) {
  switch (status.type) {
    case 'error':
      return (
        <ErrorAlert key={status.type} title={status.message}>
          {status.items.length && (
            <ul>
              {status.items.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          )}
        </ErrorAlert>
      );
    case 'success':
      return (
        <div className="border-solid border-l-4 border-green bg-green-lightest text-green-darker mb-4 p-4">
          <p className="font-bold mb-2">{status.message}</p>
          {status.items.length && (
            <ul>
              {status.items.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          )}
        </div>
      );

    default:
      return null;
  }
}

function formatEquipments(shipment) {
  const equipments = shipment.stops[0].equipments || [];
  if (equipments.length === 0) {
    return t('NONE');
  }

  return (
    <ul>
      {equipments.map((e) => (
        <li className="mb-2" key={e._id}>
          {e.equipment_id}
        </li>
      ))}
    </ul>
  );
}

function mapDateValue(value) {
  if (!value) {
    return null;
  }

  if (value instanceof moment) {
    return value;
  }

  return moment(value);
}

function renderDefaultColumns(shipment) {
  const { _id: id } = shipment;

  const idKey = `id_${id}`;
  const shipperNameKey = `shipper_${id}`;
  const equipmentsKey = `equipments_${id}`;
  const shipmentLink = `/shipments/${id}`;

  return [
    <td key={idKey}>
      <a href={shipmentLink} target="_blank" rel="noreferrer">
        {shipment.shipment_id}
      </a>
    </td>,
    <td key={shipperNameKey}>{shipment.shipper_name}</td>,
    <td key={equipmentsKey}>{formatEquipments(shipment)}</td>,
  ];
}

export default withTranslation()(ShipmentsGroup);
